import { format } from 'date-fns';
import { Map, fromJS } from 'immutable';

import { mapListToRecords } from '@sophocles/entities/entityUtils';
import { Rma } from '@sophocles/entities/Rma/rmaRecord';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, dataTableParamFormatter, genericErrorAction, mapToQueryParams } from '@sophocles/utils';

/* Receive Actions */
export const RECEIVE_RMAS = 'RECEIVE_RMAS';
function receiveRmas(json) {
  return {
    type: RECEIVE_RMAS,
    rmas: mapListToRecords(json, Rma),
  };
}

/* Request Actions */
// starter request
export const REQUEST_RMA = 'REQUEST_RMA';
function requestRma(rma_id) {
  return {
    type: REQUEST_RMA,
    rma_id,
  };
}

export const REQUEST_RMAS_FOR_SITE_GROUP = 'REQUEST_RMAS_FOR_SITE_GROUP';
function requestRmasForSiteGroup(site_group_id) {
  return {
    type: REQUEST_RMAS_FOR_SITE_GROUP,
    site_group_id,
  };
}

export const REQUEST_RMAS_FOR_CUSTOMER = 'REQUEST_RMAS_FOR_CUSTOMER';
function requestRmasForCustomer(customer_id) {
  return {
    type: REQUEST_RMAS_FOR_CUSTOMER,
    customer_id,
  };
}

export const REQUEST_ALL_RMAS = 'REQUEST_ALL_RMAS';
function requestAllRmas() {
  return {
    type: REQUEST_ALL_RMAS,
  };
}

/* Fetch Actions */
// starter fetch

export function fetchRma(rma_id) {
  return dispatch => {
    dispatch(requestRma(rma_id));
    return apiClient
      .get(`/rmas/${rma_id}/`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => {
        // Re-throw after dispatching so callers' .catch blocks run
        // instead of receiving the action object as a Map (the resolved
        // value would otherwise be `{ type, error, caughtAction }` and
        // crash on `.get(...)` downstream).
        dispatch(genericErrorAction(error, REQUEST_RMA));
        throw error;
      });
  };
}

export function fetchCustomerRmaPackingList(rma_id) {
  return dispatch =>
    apiClient
      .get(`/rmas/${rma_id}/packinglist/`)
      .then(checkHttpStatus)
      .then(response => response.data)
      .catch(error => dispatch(genericErrorAction(error, REQUEST_RMA)));
}

const completeUrl = (
  url,
  sort,
  sortDesc,
  search,
  statusFilter,
  startDate,
  endDate,
  qsFilter,
  readyToInvoiceFilter,
  needsPhotoFilter,
) => {
  let fetchString = url;

  if (sort) {
    fetchString += `&o=${sortDesc ? '-' : ''}${sort}`;
  }

  if (search) {
    fetchString += `&text_search=${search}`;
  }

  if (statusFilter) {
    fetchString += `&status=${statusFilter}`;
  }

  if (qsFilter) {
    fetchString += '&is_quick_ship=True';
  }

  if (readyToInvoiceFilter) {
    fetchString += '&ready_to_invoice=True';
  }

  if (needsPhotoFilter) {
    fetchString += '&needs_photo=True';
  }

  if (startDate) {
    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    fetchString += `&update_time__gte=${formattedStartDate}`;
  }

  if (endDate) {
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');
    fetchString += `&update_time__lte=${formattedEndDate}`;
  }

  return fetchString;
};

export function fetchRmasForSiteGroup(
  sitegroup_id,
  statusFilter,
  startDate,
  endDate,
  qsFilter,
  readyToInvoiceFilter,
  needsPhotoFilter,
) {
  return ({ limit, offset, search, sort, sortDesc }) =>
    dispatch => {
      dispatch(requestRmasForSiteGroup(sitegroup_id));
      const fetchString = completeUrl(
        `/rmas/?site_group=${sitegroup_id}&limit=${limit}&offset=${offset}`,
        sort,
        sortDesc,
        search,
        statusFilter,
        startDate,
        endDate,
        qsFilter,
        readyToInvoiceFilter,
        needsPhotoFilter,
      );

      return apiClient
        .get(fetchString)
        .then(checkHttpStatus)
        .then(response => fromJS(response.data))
        .catch(error => dispatch(genericErrorAction(error, REQUEST_RMA)));
    };
}

export function fetchRmasForCustomer(
  customer_id,
  statusFilter,
  startDate,
  endDate,
  qsFilter,
  readyToInvoiceFilter,
  needsPhotoFilter,
) {
  return ({ limit, offset, search, sort, sortDesc }) =>
    dispatch => {
      dispatch(requestRmasForCustomer(customer_id));
      const fetchString = completeUrl(
        `/rmas/?site_group__customer__id=${customer_id}&limit=${limit}&offset=${offset}`,
        sort,
        sortDesc,
        search,
        statusFilter,
        startDate,
        endDate,
        qsFilter,
        readyToInvoiceFilter,
        needsPhotoFilter,
      );

      return apiClient
        .get(fetchString)
        .then(checkHttpStatus)
        .then(response => fromJS(response.data))
        .catch(error => dispatch(genericErrorAction(error, REQUEST_RMA)));
    };
}

export function fetchAllRmas(statusFilter, startDate, endDate, qsFilter, readyToInvoiceFilter, needsPhotoFilter) {
  return ({ limit, offset, search, sort, sortDesc }) =>
    dispatch => {
      dispatch(requestAllRmas());
      const fetchString = completeUrl(
        `/rmas/?limit=${limit}&offset=${offset}`,
        sort,
        sortDesc,
        search,
        statusFilter,
        startDate,
        endDate,
        qsFilter,
        readyToInvoiceFilter,
        needsPhotoFilter,
      );

      return apiClient
        .get(fetchString)
        .then(checkHttpStatus)
        .then(response => fromJS(response.data))
        .catch(error => dispatch(genericErrorAction(error, REQUEST_RMA)));
    };
}

export function fetchAllRmasForDataTable(filterObject) {
  return tableParams => dispatch => {
    const params = Map(filterObject).merge(dataTableParamFormatter(tableParams));
    return apiClient
      .get(`/rmas/?${mapToQueryParams(params)}`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_ALL_RMAS)));
  };
}

export function fetchRmasWithTrackingForSiteGroup(sitegroup_id, limit) {
  return dispatch => {
    dispatch(requestRmasForSiteGroup(sitegroup_id));
    return apiClient
      .get('/rmas/', {
        params: { site_group: sitegroup_id, limit: limit, has_shipping_cost: true, o: '-shipping_date' },
      })
      .then(checkHttpStatus)
      .then(response => fromJS(response.data))
      .catch(error => dispatch(genericErrorAction(error, REQUEST_RMA)));
  };
}

export function fetchUnProcessedRmasForRadio(radio) {
  return dispatch =>
    apiClient
      .get(`/rmas/?radio_returned_by_user=${radio}&status__in=0,2&limit=50`)
      .then(checkHttpStatus)
      .then(response => fromJS(response.data.results))
      .catch(error => dispatch(genericErrorAction(error, 'REQUEST_UNPROCESSED_RMAS')));
}
